import { useApi } from '@classgo/app-kit';
import { gradingScaleInputSchema } from '@classgo/shared';
import { Button, Input, Select, useToast } from '@classgo/ui';
import { useQueryClient } from '@tanstack/react-query';

import { academicApi } from '../academic/api';
import type { GradingScaleView, NationalBand } from '../academic/types';
import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { FormShell } from './form-shell';

const COLOMBIA: NationalBand[] = [
  { label: 'Bajo', from: 0, to: 59.99 },
  { label: 'Básico', from: 60, to: 79.99 },
  { label: 'Alto', from: 80, to: 89.99 },
  { label: 'Superior', from: 90, to: 100 },
];

type BandRow = { label: string; from: string; to: string };

const toRows = (bands: readonly NationalBand[]): BandRow[] =>
  bands.map((band) => ({ label: band.label, from: String(band.from), to: String(band.to) }));

const score = (min: number, max: number, percent: number) =>
  Math.round((min + ((max - min) * percent) / 100) * 10) / 10;

/**
 * The school's grading scale and its equivalence with the national bands (Decreto 1290): the
 * preview shows which school scores fall in each band, so a rector sees the mapping before
 * saving.
 */
export const GradingScaleForm = ({
  yearId,
  current,
}: {
  yearId: string;
  current: GradingScaleView | null;
}) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const form = useForm({
    schema: gradingScaleInputSchema,
    initial: {
      kind: current?.kind ?? ('numeric' as GradingScaleView['kind']),
      minScore: String(current?.minScore ?? 1),
      maxScore: String(current?.maxScore ?? 5),
      passMark: String(current?.passMark ?? 3),
      bands: toRows(current?.nationalBands ?? COLOMBIA),
    },
    toInput: (values) => ({
      kind: values.kind,
      minScore: Number(values.minScore),
      maxScore: Number(values.maxScore),
      passMark: Number(values.passMark),
      nationalBands: values.bands.map((band) => ({
        label: band.label.trim(),
        from: Number(band.from),
        to: Number(band.to),
      })),
    }),
  });
  const min = Number(form.values.minScore);
  const max = Number(form.values.maxScore);
  const previewable = Number.isFinite(min) && Number.isFinite(max) && max > min;
  const setBand = (index: number, key: keyof BandRow, value: string) =>
    form.set(
      'bands',
      form.values.bands.map((band, i) => (i === index ? { ...band, [key]: value } : band)),
    );
  const error = (key: 'kind' | 'minScore' | 'maxScore' | 'passMark') =>
    form.fieldError(key) ? { error: form.fieldError(key) } : {};

  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      submitLabel={t('scale.save')}
      onSubmit={(event) => {
        event.preventDefault();
        void form.submit(async (input) => {
          await academicApi(api).putGradingScale(yearId, input);
          await queryClient.invalidateQueries({ queryKey: ['school', 'grading-scale', yearId] });
          toast.show({ tone: 'success', title: t('scale.saved') });
        });
      }}
    >
      {current ? null : <p className="text-sm text-gray-500">{t('scale.notConfigured')}</p>}
      <div className="grid gap-4 sm:grid-cols-4">
        <Select
          label={t('scale.field.kind')}
          value={form.values.kind}
          onChange={(event) => form.set('kind', event.target.value as GradingScaleView['kind'])}
          options={[
            { value: 'numeric', label: t('scale.kind.numeric') },
            { value: 'qualitative', label: t('scale.kind.qualitative') },
          ]}
          {...error('kind')}
        />
        <Input
          label={t('scale.field.minScore')}
          inputMode="decimal"
          value={form.values.minScore}
          onChange={(event) => form.set('minScore', event.target.value)}
          {...error('minScore')}
        />
        <Input
          label={t('scale.field.maxScore')}
          inputMode="decimal"
          value={form.values.maxScore}
          onChange={(event) => form.set('maxScore', event.target.value)}
          {...error('maxScore')}
        />
        <Input
          label={t('scale.field.passMark')}
          inputMode="decimal"
          value={form.values.passMark}
          onChange={(event) => form.set('passMark', event.target.value)}
          {...error('passMark')}
        />
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{t('scale.bands')}</h3>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => form.set('bands', toRows(COLOMBIA))}
          >
            {t('scale.bands.reset')}
          </Button>
        </div>
        <div className="space-y-3" role="group" aria-label={t('scale.bands')}>
          {form.values.bands.map((band, index) => (
            <div key={index} className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_10rem] sm:items-end">
              <Input
                label={`${t('scale.band.label')} ${index + 1}`}
                value={band.label}
                onChange={(event) => setBand(index, 'label', event.target.value)}
              />
              <Input
                label={`${t('scale.band.from')} ${index + 1}`}
                inputMode="decimal"
                value={band.from}
                onChange={(event) => setBand(index, 'from', event.target.value)}
              />
              <Input
                label={`${t('scale.band.to')} ${index + 1}`}
                inputMode="decimal"
                value={band.to}
                onChange={(event) => setBand(index, 'to', event.target.value)}
              />
              <p className="pb-2 text-sm" data-testid={`band-preview-${index}`}>
                <span className="block text-xs text-gray-500">{t('scale.preview')}</span>
                <span className="font-medium text-gray-900">
                  {previewable
                    ? t('scale.preview.range', {
                        from: score(min, max, Number(band.from)).toLocaleString('es-CO'),
                        to: score(min, max, Number(band.to)).toLocaleString('es-CO'),
                      })
                    : '—'}
                </span>
              </p>
            </div>
          ))}
        </div>
        {form.fieldError('nationalBands' as never) ? (
          <p role="alert" className="mt-1 text-sm text-danger-700">
            {form.fieldError('nationalBands' as never)}
          </p>
        ) : null}
      </div>
    </FormShell>
  );
};
